import { cx } from "@emotion/css";
import { ChartColumn } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DeckStatsType } from "src/api/flashcardDecks";
import Box from "src/components/Default/Main/Box";
import {
	Modal,
	ModalHeader,
	ModalStickyFooter,
} from "src/components/Modals/Components/ModalsComponents";
import { DeckFlashcardsStatsList } from "src/components/Modals/StatisticsModals/DeckFlashcardsStatsList";
import isMobile from "src/helpers/isMobile";
import { useFontSize } from "src/hooks/useFontSize";
import { useKeyboardOpen } from "src/hooks/useKeyboardOpen";

interface DefaultDeckStatisticsModalProps {
	onClose: () => void;
	isLoading?: boolean;
	chart: ReactNode;
	statsList: ReactNode;
	primaryAction: ReactNode;
	deckId: number;
	deckType: DeckStatsType;
	startDate?: string;
	endDate?: string;
	hidePrimaryAction?: boolean;
	datePicker?: ReactNode;
	subTabs?: ReactNode;
}

const TABS = {
	STATS: 0,
	FLASHCARDS: 1,
} as const;

export const DefaultDeckStatisticsModal = ({
	onClose,
	isLoading,
	chart,
	statsList,
	primaryAction,
	deckId,
	deckType,
	startDate,
	endDate,
	hidePrimaryAction,
	datePicker,
	subTabs,
}: DefaultDeckStatisticsModalProps) => {
	const { t } = useTranslation();
	const { modalHeaderFontSizeClass } = useFontSize();
	const [selectedTab, setSelectedTab] = useState<number>(TABS.STATS);
	const keyboardOpen = useKeyboardOpen();

	return (
		<Modal onClose={onClose} isLoading={isLoading} maxWidth="sm">
			<ModalHeader
				Icon={ChartColumn}
				title={t("modals.deckStatistics.statsHeader", "Statystyki")}
				hasCloseButton
			/>
			<Box
				className="border-none box-shadow-none p-0 mt-24"
				headerClassName="mb-0 border-bottom-1 pb-12"
				tabs={[
					t("modals.deckStatistics.tabs.stats", "Statystyki"),
					t("modals.deckStatistics.tabs.flashcards", "Fiszki"),
				]}
				selectedTab={selectedTab}
				onTabsChange={setSelectedTab}
				tabTestIds={["deck-stats-tab-stats", "deck-stats-tab-flashcards"]}
				additionalTabsActions={datePicker ? () => datePicker : undefined}
			/>

			{subTabs && <div className="mt-16">{subTabs}</div>}

			{selectedTab === TABS.STATS ? (
				<div className="flex flex-col gap-16 mt-24 container-inline">
					<span className={cx(modalHeaderFontSizeClass, "text-dark-grey")}>
						{t("modals.deckStatistics.flashcardsCount", "Liczba fiszek")}
					</span>
					<div className="chart-with-legend">
						{chart}
						<div className="flex flex-col gap-12 flex-1 w-100">{statsList}</div>
					</div>
				</div>
			) : (
				<DeckFlashcardsStatsList
					deckId={deckId}
					deckType={deckType}
					startDate={startDate}
					endDate={endDate}
				/>
			)}

			{!hidePrimaryAction && !(isMobile() && keyboardOpen) && (
				<ModalStickyFooter vertical className="mt-24 pt-16 border-top-1 gap-8">
					{primaryAction}
				</ModalStickyFooter>
			)}
		</Modal>
	);
};
